import React, { useState } from "react";
import { useUser, UserProfile } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import StarryBackground from "@/components/layout/StarryBackground";

export default function ForcePasswordChange() {
  const { user } = useUser();
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleContinue = async () => {
    if (!user) return;
    setClearing(true);
    setError(null);
    try {
      await user.update({
        unsafeMetadata: {
          ...user.unsafeMetadata,
          must_change_password: false,
        },
      });
      // After the update the parent (WorkspaceHome) will re-render and
      // show AppShell because the flag is now cleared.
    } catch (err: any) {
      setError(err?.message || "Unable to update profile. Please try again.");
      setClearing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex h-svh w-svw flex-col items-center justify-center overflow-auto bg-transparent text-white">
      <StarryBackground variant="full" showParticles={true} showPrompts={false} />

      <div className="relative z-10 flex w-full max-w-2xl flex-col items-center gap-6 px-6 py-12">
        <div className="w-full rounded-3xl border border-white/10 bg-black/30 p-8 text-center shadow-2xl backdrop-blur-md">
          <h2 className="mb-2 text-xl font-semibold">
            Update Your Password
          </h2>
          <p className="mb-6 text-sm text-white/70">
            Please update your default password before continuing.
            Use the security section below to set a new password.
          </p>

          <div className="mx-auto mb-6 max-w-lg overflow-hidden rounded-xl [&_.cl-rootBox]:w-full [&_.cl-card]:bg-transparent [&_.cl-card]:shadow-none">
            <UserProfile
              routing="virtual"
              appearance={{
                elements: {
                  rootBox: "w-full",
                  card: "bg-transparent shadow-none border-none",
                  navbar: "hidden",
                  navbarMobileMenuButton: "hidden",
                  headerTitle: "hidden",
                  headerSubtitle: "hidden",
                  profileSectionPrimaryButton: "text-purple-400",
                },
              }}
            />
          </div>

          {error && (
            <p className="mb-4 text-sm text-red-300">{error}</p>
          )}

          <Button
            onClick={handleContinue}
            disabled={clearing}
            className="w-full max-w-xs border-purple-500/50 hover:bg-purple-500/10"
          >
            {clearing ? "Saving..." : "Continue to App"}
          </Button>

          <p className="mt-3 text-[11px] text-white/40">
            You can always update your password later from your profile settings.
          </p>
        </div>
      </div>
    </div>
  );
}
